// WebSocket client for agent runs. Replaces the blocking HTTP fetch so long
// runs survive CloudFront's ~60s timeout. Opens a connection, streams each
// `agent_event` to onEvent, and resolves on `agent_done` (or rejects on
// `agent_error` / connection failure). The socket is closed once the run settles.

import { getServerUri, getSocketUri } from "./uriUtility";

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function makeDisconnectError(message) {
  const error = new Error(message);
  error.isDisconnect = true;
  return error;
}

/**
 * List checkpoint history for a version/scope.
 *
 * @param {string} versionId
 * @param {object} [opts]
 * @param {string} [opts.agentType]
 */
export async function listAgentCheckpoints(versionId, { agentType } = {}) {
  const params = {
    accessToken: localStorage.getItem("session.accessToken"),
    versionId,
  };
  if (agentType) params.agentType = agentType;

  const res = await fetch(getServerUri("agent/list-checkpoints"), {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams(params),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `List checkpoints request failed (${res.status})`);
  }

  return res.json();
}

/**
 * Restore a checkpoint for a version/scope.
 *
 * @param {string} versionId
 * @param {object} [opts]
 * @param {string} [opts.agentType]
 * @param {string} [opts.checkpointId]
 */
export async function restoreAgentCheckpoint(versionId, { agentType, checkpointId } = {}) {
  const params = {
    accessToken: localStorage.getItem("session.accessToken"),
    versionId,
  };
  if (agentType) params.agentType = agentType;
  if (checkpointId) params.checkpointId = checkpointId;

  const res = await fetch(getServerUri("agent/restore-checkpoint"), {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams(params),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Restore checkpoint request failed (${res.status})`);
  }
  return data;
}

/**
 * Fetch the in-memory run status for a version from the server.
 *
 * @param {string} versionId
 * @returns {Promise<object>} { status: "idle"|"running"|"completed"|"failed", ... }
 */
export async function fetchRunStatus(versionId) {
  const res = await fetch(getServerUri("agent/run-status"), {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      accessToken: localStorage.getItem("session.accessToken"),
      versionId,
    }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Run status request failed (${res.status})`);
  }

  return res.json();
}

/**
 * Tell the server a settled run has been consumed so it is not replayed later.
 *
 * @param {string} versionId
 */
export async function acknowledgeRun(versionId) {
  try {
    const res = await fetch(getServerUri("agent/acknowledge-run"), {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        accessToken: localStorage.getItem("session.accessToken"),
        versionId,
      }),
    });
    if (!res.ok) return false;
    const data = await res.json().catch(() => ({}));
    return Boolean(data.ok);
  } catch (_err) {
    return false;
  }
}

/**
 * Poll until a version's run is no longer in the "running" state.
 *
 * @param {string} versionId
 * @param {object} [opts]
 * @param {number} [opts.intervalMs=3000]
 * @param {number} [opts.maxMs=540000] — 9 min, above server AGENT_MAX_MS
 * @returns {Promise<object>} final run-status payload
 */
export async function pollRunUntilSettled(versionId, { intervalMs = 3000, maxMs = 9 * 60 * 1000 } = {}) {
  const deadline = Date.now() + maxMs;

  while (Date.now() < deadline) {
    const status = await fetchRunStatus(versionId);
    if (status.status !== "running") {
      return status;
    }
    await sleep(intervalMs);
  }

  throw new Error("Timed out waiting for agent run to finish");
}

/**
 * Run an agent over the WebSocket server.
 *
 * @param {object} opts
 * @param {string} [opts.agentType] — "playengine" (default, when omitted) | "games"
 * @param {"edit"|"create"} opts.mode
 * @param {string} opts.versionId
 * @param {string} opts.prompt
 * @param {boolean} [opts.continueRun] — resume the version's paused agent task from
 *   its stored handoff; prompt becomes an optional extra instruction
 * @param {function} [opts.onEvent] — called with each streamed agent loop event
 * @returns {Promise<object>} resolves with the final agent result (summary, changes,
 *   debugLog, updatedPlayEngineConfig, ...). A paused segment resolves with
 *   `.paused === true` (and `.canContinue`). Rejects with an Error; on a handled
 *   agent failure the Error carries `.restored`, `.restoredPlayEngineConfig`, and `.restoredDashboardMeta`.
 *   On disconnect before completion, rejects with `.isDisconnect === true`.
 */
export function runAgentOverSocket({ agentType, mode, versionId, prompt, continueRun, onEvent }) {
  return new Promise((resolve, reject) => {
    let settled = false;
    let socket;

    const settle = (fn, value) => {
      if (settled) return;
      settled = true;
      try {
        if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
          socket.close();
        }
      } catch (_err) {
        // ignore close errors
      }
      fn(value);
    };

    try {
      socket = new WebSocket(getSocketUri());
    } catch (err) {
      reject(err);
      return;
    }

    socket.onopen = () => {
      const runMessage = { type: "agent_run", mode, versionId, prompt };
      if (agentType) runMessage.agentType = agentType;
      if (continueRun) runMessage.continueRun = true;
      socket.send(JSON.stringify(runMessage));
    };

    socket.onmessage = (evt) => {
      let msg;
      try {
        msg = JSON.parse(evt.data);
      } catch (_err) {
        return;
      }

      switch (msg.type) {
        case "agent_event":
          if (typeof onEvent === "function" && msg.event) onEvent(msg.event);
          break;

        case "agent_done":
          settle(resolve, msg.result);
          break;

        case "agent_error": {
          const error = new Error(msg.error || "Agent run failed");
          error.restored = Boolean(msg.restored);
          error.restoredPlayEngineConfig = msg.restoredPlayEngineConfig || null;
          error.restoredDashboardMeta = msg.restoredDashboardMeta || null;
          error.restoredVersions = msg.restoredVersions || null;
          error.code = msg.code || null;
          error.creditCost = msg.creditCost != null ? msg.creditCost : null;
          error.balanceAfter = msg.balanceAfter != null ? msg.balanceAfter : null;
          error.pricingExplanation = msg.pricingExplanation || null;
          settle(reject, error);
          break;
        }

        case "error": {
          // Connection-level error (e.g. auth rejection before any run starts).
          settle(reject, new Error(msg.error || "WebSocket error"));
          break;
        }

        // "connected", "keepalive", "pong" — informational, ignore.
        default:
          break;
      }
    };

    socket.onerror = () => {
      settle(reject, makeDisconnectError("WebSocket connection error"));
    };

    socket.onclose = () => {
      settle(reject, makeDisconnectError("WebSocket connection closed before the agent run finished"));
    };
  });
}

/**
 * Fetch pinned agent messages for a base (games agent).
 *
 * @param {string} baseId
 */
export async function fetchBaseAgentMessages(baseId) {
  const res = await fetch(getServerUri("base/findAgentMessages"), {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      accessToken: localStorage.getItem("session.accessToken"),
      baseID: baseId,
    }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Fetch pinned messages failed (${res.status})`);
  }
  return data;
}

/**
 * Create or update a pinned agent message for a base.
 *
 * @param {string} baseId
 * @param {{ messageId?: string, message: string, isAdminMessage?: boolean }} payload
 */
export async function upsertBaseAgentMessage(baseId, payload) {
  const params = {
    accessToken: localStorage.getItem("session.accessToken"),
    baseID: baseId,
    message: payload && payload.message != null ? String(payload.message) : "",
  };
  if (payload && payload.messageId) params.messageId = String(payload.messageId);
  if (payload && payload.isAdminMessage) params.isAdminMessage = "true";

  const res = await fetch(getServerUri("base/upsertAgentMessage"), {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams(params),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Save pinned message failed (${res.status})`);
  }
  return data;
}

/**
 * Delete a pinned agent message from a base.
 *
 * @param {string} baseId
 * @param {string} messageId
 */
export async function deleteBaseAgentMessage(baseId, messageId) {
  const res = await fetch(getServerUri("base/deleteAgentMessage"), {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      accessToken: localStorage.getItem("session.accessToken"),
      baseID: baseId,
      messageId: String(messageId),
    }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Delete pinned message failed (${res.status})`);
  }
  return data;
}
